import React, { useEffect, useRef } from "react";
import ClinicIcon, { ClinicIconName } from "./ClinicIcon";
import { useI18n } from "../../i18n/I18nProvider";
import { Link, useLocation } from "react-router-dom";
import "./ModuleShellHeader.css";

export type ModuleTheme =
    | "reception"
    | "doctor"
    | "manager"
    | "careflow"
    | "pharmacy"
    | "admin";

export interface ModuleTab<T extends string = string> {
    key: T;
    label: string;
    icon?: ClinicIconName;
    disabled?: boolean;
    tooltip?: string;
}

export interface AlertBadge {
    key: string;
    label: string;
    count: number;
    icon?: ClinicIconName;
    badgeColor?: string;
    onClick?: () => void;
}

interface ModuleShellHeaderProps<T extends string = string> {
    theme?: ModuleTheme;
    brandName: string;
    brandSub?: string;
    tabs?: ModuleTab<T>[];
    activeTab?: T;
    onTabChange?: (_tab: T) => void;
    alerts?: AlertBadge[];
    user?: { name: string; role?: string };
    onLogout?: () => void;
    showDate?: boolean;
    children?: React.ReactNode; // extra slot (e.g. patient search)
    className?: string;
}

function getInitials(name: string) {
    return name
        .split(" ")
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

export default function ModuleShellHeader<T extends string = string>({
    theme = "reception",
    brandName,
    brandSub,
    tabs = [],
    activeTab,
    onTabChange,
    alerts = [],
    user,
    onLogout,
    showDate = true,
    children,
    className = "",
}: ModuleShellHeaderProps<T>) {
    const { t, formatDate } = useI18n();
    const location = useLocation();
    const tabListRef = useRef<HTMLDivElement>(null);

    // Scroll active tab into view
    useEffect(() => {
        if (!tabListRef.current || !activeTab) return;
        const activeEl = tabListRef.current.querySelector<HTMLElement>(
            `[data-tabkey="${activeTab}"]`
        );
        if (activeEl) {
            activeEl.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        }
    }, [activeTab]);

    // Keyboard navigation (left/right arrow)
    const handleTabKeyDown = (
        e: React.KeyboardEvent<HTMLButtonElement>,
        idx: number
    ) => {
        const activeTabs = tabs.filter((t) => !t.disabled);
        if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
            e.preventDefault();
            const dir = e.key === "ArrowRight" ? 1 : -1;
            const currentIdx = activeTabs.findIndex((t) => t.key === tabs[idx]?.key);
            const next = (currentIdx + dir + activeTabs.length) % activeTabs.length;
            onTabChange?.(activeTabs[next].key as T);
            // focus the next button
            const buttons =
                tabListRef.current?.querySelectorAll<HTMLButtonElement>("button.msh-tab:not(:disabled)");
            if (buttons) buttons[next]?.focus();
        }
    };

    const dateStr = formatDate(new Date(), {
        weekday: "short",
        day: "numeric",
        month: "short",
    });

    return (
        <header
            className={`msh-root theme-${theme} ${className}`}
            role="banner"
        >
            {/* Navigation Controls */}
            <div className="msh-nav-controls">
                <button
                    type="button"
                    className="msh-nav-btn"
                    onClick={() => window.history.back()}
                    title={t("app_go_back") || "Back"}
                >
                    <ClinicIcon name="arrow-left" size={20} strokeWidth={2.5} />
                </button>
                <Link
                    to="/careflow"
                    className="msh-nav-btn home-btn"
                    title="CareFlow Home"
                >
                    <ClinicIcon name="home" size={20} strokeWidth={2.5} />
                </Link>
            </div>

            {/* Brand */}
            <div className="msh-brand">
                <div className="msh-brand-dot" />
                <div>
                    <div className="msh-brand-name">{brandName}</div>
                    {brandSub && <div className="msh-brand-sub">{brandSub}</div>}
                </div>
            </div>

            {/* Global Module Switcher */}
            <div className="msh-global-nav">
                <Link
                    to="/doctor"
                    className={`msh-nav-item${location.pathname.startsWith("/doctor") ? " active" : ""}`}
                    title={t("doctor_brand_name") || "Doctor Hub"}
                >
                    <ClinicIcon name="doctor" size={24} strokeWidth={2} />
                </Link>
                <Link
                    to="/manager"
                    className={`msh-nav-item${location.pathname.startsWith("/manager") ? " active" : ""}`}
                    title={t("manager_brand_name") || "Management"}
                >
                    <ClinicIcon name="manager" size={24} strokeWidth={2} />
                </Link>
                <Link
                    to="/followups"
                    className={`msh-nav-item${location.pathname.startsWith("/followups") ? " active" : ""}`}
                    title={t("followup_brand_name") || "Treatment Followups"}
                >
                    <ClinicIcon name="followup" size={24} strokeWidth={2} />
                </Link>
            </div>

            {/* Children slot (e.g. patient search) */}
            {children && <div className="msh-children">{children}</div>}

            {/* Tabs */}
            {tabs.length > 0 && (
                <nav
                    className="msh-tabs"
                    role="tablist"
                    aria-label={t("module_navigation_aria", { module: brandName })}
                    ref={tabListRef}
                >
                    {tabs.map((tab, idx) => (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            data-tabkey={tab.key}
                            className={`msh-tab${activeTab === tab.key ? " active" : ""}`}
                            aria-selected={activeTab === tab.key}
                            aria-controls={`panel-${tab.key}`}
                            disabled={tab.disabled}
                            title={tab.tooltip ?? tab.label}
                            onClick={() => onTabChange?.(tab.key as T)}
                            onKeyDown={(e) => handleTabKeyDown(e, idx)}
                        >
                            {tab.icon && (
                                <ClinicIcon
                                    name={tab.icon}
                                    size={15}
                                    strokeWidth={activeTab === tab.key ? 2.2 : 1.8}
                                />
                            )}
                            <span className="msh-tab-label">{tab.label}</span>
                        </button>
                    ))}
                </nav>
            )}

            {/* Right section */}
            <div className="msh-right">
                {showDate && <span className="msh-date">{dateStr}</span>}

                {alerts.map((a) => (
                    <button
                        key={a.key}
                        type="button"
                        className="msh-alert-btn"
                        onClick={a.onClick}
                        title={`${a.label}: ${a.count}`}
                    >
                        {a.icon && <ClinicIcon name={a.icon} size={14} />}
                        <span>{a.label}</span>
                        <span
                            className={`msh-badge${a.count === 0 ? " zero" : ""}`}
                            style={a.count > 0 && a.badgeColor ? { background: a.badgeColor } : undefined}
                        >
                            {a.count}
                        </span>
                    </button>
                ))}

                {user && (
                    <>
                        <div className="msh-divider" />
                        <div className="msh-user">
                            <div>
                                <div className="msh-user-name">{user.name}</div>
                                {user.role && <div className="msh-user-role">{user.role}</div>}
                            </div>
                            <div className="msh-avatar" title={user.name}>
                                {getInitials(user.name)}
                            </div>
                        </div>
                    </>
                )}

                {onLogout && (
                    <button
                        type="button"
                        className="msh-logout-btn"
                        onClick={onLogout}
                        title={t("common_logout")}
                    >
                        <ClinicIcon name="logout" size={13} />
                        <span>{t("common_logout")}</span>
                    </button>
                )}
            </div>
        </header>
    );
}
